import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../../../httpClient';
import ChevronIcon from '../../../images/chevron-down-12.svg';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';

const HARDWOOD_FRAME_TYPES = ['Hardwood Frame', 'Hardwood Float Canvas Frame'];
const CIRCLE_OVAL_FRAME_PATTERN = /\b(Circle|Oval)\s+Frame\b/i;
const isCircleOrOvalFrameType = (text) => CIRCLE_OVAL_FRAME_PATTERN.test(text || '');

const MAT_UPSTREAM_KEYS = new Set(['frameType', 'frame', 'frameMat']);

const NO_MAT_PATTERN = /\bno\s*mat\b/i;
const CIRCLE_DECKLE_PATTERN = /circle\s*deckle/i;

const MAT_TYPE_LABELS = {
  '8-ply': '8-Ply',
  'double-mat-4-ply': 'Double Mat 4-Ply',
};

const OPENING_SHAPE_LABELS = {
  standard: 'Standard',
  circle: 'Circle',
  oval: 'Oval',
};

// A frame mat selection counts as "no mat" when the literal 'none' sentinel is
// chosen or when the backend mat name resolves to "No Mat". In that case the
// Mat Type and Mat Opening Shape menus do not apply.
const isNoMatFrameMat = (input) => {
  const value = input?.selectedValue;
  if (!value || value === 'default') return false;
  if (String(value).toLowerCase() === 'none') return true;
  return (
    NO_MAT_PATTERN.test(input?.selectedValueText || '') ||
    NO_MAT_PATTERN.test(String(value))
  );
};

const FramingFormInputs = ({ quantityRef }) => {
  const {
    form,
    setForm,
    defaultOptions,
    resetPrintSizeForm,
    setPrintSizeLoading,
    processPrintSizeData,
    setFinishingServiceLoading,
    setFrameInputsLoading,
    processFinishingCategoriesData,
    processFinishingServicesData,
    processFinishingDetailsData,
    processFinishingServiceChange,
    processFrameMatPricingData,
    processFrameSelection,
    processFrameMatSelection,
    getBaseMeta,
    resetFormInputs,
    getMetaResets,
    finishingCategoriesData,
  } = useProductSelectionStore();

  const paperTypeInput = form?.inputs?.paperType;
  const printSizeInput = form?.inputs?.printSize;
  const finishingServiceInput = form?.inputs?.finishingService;
  const frameTypeInput = form?.inputs?.frameType;
  const frameInput = form?.inputs?.frame;
  const frameMatInput = form?.inputs?.frameMat;
  const acrylicInput = form?.inputs?.acrylic;
  const frameStainInput = form?.inputs?.frameStain;

  const matType = form?.meta?.matType || 'default';
  const matOpeningShape = form?.meta?.matOpeningShape || 'default';

  // A real frame mat (i.e. not "No Mat") is what gates the Mat Type and Mat
  // Opening Shape menus.
  const hasRealFrameMat =
    !!frameMatInput?.selectedValue &&
    frameMatInput.selectedValue !== 'default' &&
    !isNoMatFrameMat(frameMatInput);

  // Circle deckle prints can only be cropped/matted as a circle.
  const isCircleDeckle = CIRCLE_DECKLE_PATTERN.test(
    paperTypeInput?.selectedValueText || '',
  );

  const setMatType = (value) => {
    setForm((prev) => ({
      ...prev,
      meta: {
        ...prev.meta,
        matType: value,
        matOpeningShape: undefined,
      },
    }));
  };

  const setMatOpeningShape = (value) => {
    setForm((prev) => ({
      ...prev,
      meta: { ...prev.meta, matOpeningShape: value },
    }));
  };

  const paperTypeSelectedValue = paperTypeInput?.selectedValue;
  const printSizeSelectedValue = printSizeInput?.selectedValue;
  const finishingCategoryId = form?.meta?.finishingCategoryId;
  const finishingServiceId = form?.meta?.finishingServiceId;
  const frameTypeText = frameTypeInput?.selectedValueText;
  const isHardwoodFrame = HARDWOOD_FRAME_TYPES.includes(frameTypeText);
  const isCircleOrOvalFrame = isCircleOrOvalFrameType(frameTypeText);

  // --- Queries ---

  const printSizesQuery = useQuery({
    queryKey: ['printSizes', paperTypeSelectedValue],
    queryFn: () => httpClient.get(`products/${paperTypeSelectedValue}/print_sizes`),
    enabled: !!paperTypeSelectedValue && paperTypeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processPrintSizeData(response);
    },
  });

  const finishingCategoriesQuery = useQuery({
    queryKey: ['finishingCategories', printSizeSelectedValue],
    queryFn: () =>
      httpClient.get(
        `products/print_sizes/${printSizeSelectedValue}/finishing_categories`,
      ),
    enabled: !!printSizeSelectedValue && printSizeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingCategoriesData(response);
    },
  });

  const finishingServicesQuery = useQuery({
    queryKey: ['finishingServices', printSizeSelectedValue, finishingCategoryId],
    queryFn: () =>
      httpClient.get(
        `products/print_sizes/${printSizeSelectedValue}/finishing_categories/${finishingCategoryId}/finishing_services`,
      ),
    enabled: !!printSizeSelectedValue && !!finishingCategoryId,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingServicesData(response);
    },
  });

  useQuery({
    queryKey: ['finishingDetailsOptions', finishingServiceId],
    queryFn: () =>
      httpClient.get(`products/${finishingServiceId}/finishing_details`),
    enabled: !!finishingServiceId,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingDetailsData(response);
    },
  });

  const frameMatsQuery = useQuery({
    queryKey: ['frameMats', printSizeSelectedValue, finishingCategoryId],
    queryFn: () =>
      httpClient.get(
        `products/frame_mats?print_size_guid=${printSizeSelectedValue}&finishing_category_guid=${finishingCategoryId}`,
      ),
    enabled: hasRealFrameMat && !!printSizeSelectedValue && !!finishingCategoryId,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFrameMatPricingData(response);
    },
  });

  const frameMatRows = frameMatsQuery.data?.data ?? [];

  const matTypeOptions = Array.from(
    new Set(frameMatRows.map((row) => row.attributes.matType)),
  )
    .filter((value) => value !== '4-ply')
    .map((value) => ({
      value,
      text: MAT_TYPE_LABELS[value] || value,
    }));

  const matOpeningShapeOptions = Array.from(
    new Set(
      frameMatRows
        .filter((row) => row.attributes.matType === matType)
        .map((row) => row.attributes.openingShape),
    ),
  )
    .filter((value) => !isCircleDeckle || value === 'circle')
    .map((value) => ({
      value,
      text: OPENING_SHAPE_LABELS[value] || value,
    }));

  const selectedFrameMatPricing = frameMatRows.find(
    (row) =>
      row.attributes.matType === matType &&
      row.attributes.openingShape === matOpeningShape,
  );
  const frameMatPricingGuid = selectedFrameMatPricing?.id;

  useEffect(() => {
    if (form?.meta?.frameMatPricingGuid === frameMatPricingGuid) return;
    setForm((prev) => ({
      ...prev,
      meta: { ...prev.meta, frameMatPricingGuid },
    }));
  }, [frameMatPricingGuid]);

  // --- Loading effects ---

  useEffect(() => {
    if (paperTypeSelectedValue === 'default') {
      resetPrintSizeForm();
    } else if (
      paperTypeSelectedValue &&
      paperTypeSelectedValue !== 'default' &&
      printSizesQuery.isLoading
    ) {
      setPrintSizeLoading(true);
    }
  }, [paperTypeSelectedValue, printSizesQuery.isLoading]);

  useEffect(() => {
    if (
      printSizeSelectedValue &&
      printSizeSelectedValue !== 'default' &&
      finishingCategoriesQuery.isLoading
    ) {
      setFinishingServiceLoading(true);
    }
  }, [printSizeSelectedValue, finishingCategoriesQuery.isLoading]);

  useEffect(() => {
    if (
      finishingCategoryId &&
      finishingCategoryId !== 'default' &&
      finishingServicesQuery.isLoading
    ) {
      setFrameInputsLoading(true);
    }
  }, [finishingCategoryId, finishingServicesQuery.isLoading]);


  useEffect(() => {
    const options = finishingServiceInput?.options;
    if (!options || options.length === 0) return;
    if (finishingServiceInput?.selectedValue) return;

    const framingOption = options.find((opt) => opt?.text === 'Framing');
    if (!framingOption) return;

    setForm((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        finishingService: {
          ...prev.inputs.finishingService,
          selectedValue: framingOption.value,
          selectedValueText: framingOption.text,
        },
      },
    }));
  }, [finishingServiceInput?.options, finishingServiceInput?.selectedValue, setForm]);

  // --- Cascade effects (mirrored from FormInputs framing chain) ---

  useEffect(() => {
    if (finishingServiceInput?.selectedValueText === undefined) return;
    processFinishingServiceChange(finishingServiceInput.selectedValueText);
    
  }, [finishingServiceInput?.selectedValueText, finishingCategoriesData]);

  // Set finishingCategoryId from frameType
  useEffect(() => {
    if (frameTypeInput?.selectedValue === undefined) return;

    if (frameTypeInput.selectedValue === 'default') {
      setForm((prevState) => ({
        meta: {
          ...getBaseMeta(prevState),
          finishingCategoryId: undefined,
          finishingServiceId: undefined,
          finishingDetailId: undefined,
          requiredInputs: ['Frame Type'],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
          frameType: prevState.inputs.frameType,
        },
      }));
      return;
    }

    setForm((prevState) => ({
      meta: {
        ...getBaseMeta(prevState),
        finishingCategoryId: frameTypeInput.selectedValue,
        error: 'Please fill Frame, Frame Mat, Acrylic and Frame Stain',
      },
      inputs: prevState.inputs,
    }));
  }, [frameTypeInput?.selectedValue]);

  // Frame selection → frame mats
  useEffect(() => {
    if (frameInput?.selectedValue === undefined) return;
    processFrameSelection(frameInput.selectedValue, frameInput.selectedValueText);
  }, [frameInput?.selectedValue]);

  // Frame mat selection → acrylics
  useEffect(() => {
    if (frameMatInput?.selectedValue === undefined) return;
    processFrameMatSelection(
      frameMatInput.selectedValue,
      frameMatInput.selectedValueText,
    );
  }, [frameMatInput?.selectedValue]);

  // Acrylic selection → finishingServiceId
  useEffect(() => {
    if (acrylicInput?.selectedValue === undefined) return;

    if (acrylicInput.selectedValue === 'default') {
      setForm((prevState) => ({
        meta: {
          ...getBaseMeta(prevState),
          requiredInputs: [
            'Acrylic',
            ...(HARDWOOD_FRAME_TYPES.includes(
              prevState.inputs?.frameType?.selectedValueText,
            )
              ? ['Frame Stain']
              : []),
          ],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
          frameType: prevState.inputs.frameType,
          frame: prevState.inputs.frame,
          frameMat: prevState.inputs.frameMat,
          acrylic: prevState.inputs.acrylic,
          ...(HARDWOOD_FRAME_TYPES.includes(
            prevState.inputs?.frameType?.selectedValueText,
          ) && { frameStain: defaultOptions.frameStain }),
        },
      }));
      return;
    }

    if (acrylicInput.selectedValue === 'none') return;

    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...getBaseMeta(prevState),
        finishingServiceId: acrylicInput.selectedValue,
        requiredInputs: [],
      },
    }));
  }, [acrylicInput?.selectedValue]);

  // Frame stain selection → finishingDetailId
  useEffect(() => {
    if (frameStainInput?.selectedValue === undefined) return;
    if (frameStainInput.selectedValue === 'none') return;

    setForm((prevState) => ({
      meta: {
        ...getBaseMeta(prevState),
        finishingDetailId: frameStainInput.selectedValue,
        requiredInputs: [],
      },
      inputs: prevState.inputs,
    }));
  }, [frameStainInput?.selectedValue]);

  // Keep requiredInputs in sync with current selections.
  useEffect(() => {
    const isSet = (v) => v && v !== 'default';
    const paperSelected = isSet(paperTypeSelectedValue);
    const sizeSelected = isSet(printSizeSelectedValue);
    const frameTypeSelected = isSet(frameTypeInput?.selectedValue);
    const frameSelected = isSet(frameInput?.selectedValue);
    const frameMatSelected =
      isSet(frameMatInput?.selectedValue) ||
      frameMatInput?.selectedValue === 'none';
    const frameMatNotNone = hasRealFrameMat;
    const matTypeSelected = isSet(matType);
    const matOpeningShapeSelected = isSet(matOpeningShape);
    const matChainComplete =
      !frameMatNotNone ||
      (matTypeSelected &&
        (matType === 'no-front-mat' || matOpeningShapeSelected));
    const acrylicSelected =
      isSet(acrylicInput?.selectedValue) ||
      acrylicInput?.selectedValue === 'none';
    const frameStainSelected =
      isSet(frameStainInput?.selectedValue) ||
      frameStainInput?.selectedValue === 'none';

    const required = [];
    if (!paperSelected) required.push('Paper Type');
    if (!sizeSelected) required.push('Print Size');
    if (paperSelected && sizeSelected && !frameTypeSelected) {
      required.push('Frame Type');
    }
    if (frameTypeSelected && !isCircleOrOvalFrame && !frameSelected) {
      required.push('Frame');
    }
    const matGateMet = isCircleOrOvalFrame ? frameTypeSelected : frameSelected;
    if (matGateMet && !frameMatSelected) required.push('Frame Mat');
    if (frameMatNotNone && !matTypeSelected) required.push('Mat Type');
    if (
      frameMatNotNone &&
      matTypeSelected &&
      matType !== 'no-front-mat' &&
      !matOpeningShapeSelected
    ) {
      required.push('Mat Opening Shape');
    }
    if (frameMatSelected && matChainComplete && !acrylicSelected) {
      required.push('Acrylic');
    }
    const frameStainReady =
      !!frameStainInput && (frameStainInput?.options?.length || 0) > 0;
    if (
      isHardwoodFrame &&
      acrylicSelected &&
      acrylicInput?.selectedValue !== 'none' &&
      frameStainReady &&
      !frameStainSelected
    ) {
      required.push('Frame Stain');
    }

    const current = form?.meta?.requiredInputs || [];
    const matches =
      current.length === required.length &&
      current.every((v, i) => v === required[i]);
    if (matches) return;

    setForm((prev) => ({
      ...prev,
      meta: { ...prev.meta, requiredInputs: required },
    }));
  }, [
    paperTypeSelectedValue,
    printSizeSelectedValue,
    frameTypeInput?.selectedValue,
    frameInput?.selectedValue,
    frameMatInput?.selectedValue,
    hasRealFrameMat,
    matType,
    matOpeningShape,
    acrylicInput?.selectedValue,
    frameStainInput?.selectedValue,
    isHardwoodFrame,
    isCircleOrOvalFrame,
    form?.meta?.requiredInputs,
    setForm,
  ]);

  // Circle deckle prints support only a circle mat opening. Auto-select it as
  // soon as the Mat Opening Shape menu becomes applicable.
  useEffect(() => {
    if (!isCircleDeckle) return;
    if (!hasRealFrameMat) return;
    if (matType === 'no-front-mat') return;
    if (matOpeningShape === 'circle') return;
    setMatOpeningShape('circle');
  }, [isCircleDeckle, hasRealFrameMat, matType, matOpeningShape]);

  // When only one opening shape is actually offered for this mat type,
  // there's nothing for the user to pick — auto-select it instead of
  // showing a one-option dropdown (the field is hidden for this case below).
  const soleOpeningShapeValue =
    matOpeningShapeOptions.length === 1 ? matOpeningShapeOptions[0].value : undefined;
  useEffect(() => {
    if (!hasRealFrameMat) return;
    if (matType === 'no-front-mat') return;
    if (!soleOpeningShapeValue) return;
    if (matOpeningShape === soleOpeningShapeValue) return;
    setMatOpeningShape(soleOpeningShapeValue);
  }, [hasRealFrameMat, matType, soleOpeningShapeValue, matOpeningShape]);

  // --- Handlers ---

  const stripPrice = (text) => text.replace(/\(\$\d+(\.\d{1,2})?\)/g, '').trim();

  const filterPaperOption = (option) => {
    if (!option?.text) return false;
    const text = option.text.toLowerCase();
    return (
      !text.includes('canvas') &&
      !text.includes('lustre')
    );
  };

  const handlePaperTypeChange = (e) => {
    const value = e.target.value;
    const selectedText = e.target.options[e.target.selectedIndex].text;

    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...prevState.meta,
        matType: undefined,
        matOpeningShape: undefined,
      },
      inputs: {
        ...prevState.inputs,
        paperType: {
          ...prevState.inputs.paperType,
          selectedValue: value,
          selectedValueText: selectedText,
        },
        printSize: {
          ...prevState.inputs.printSize,
          selectedValue: undefined,
          selectedValueText: undefined,
          selectedValuePrice: undefined,
        },
      },
    }));
  };

  const handlePrintSizeChange = (e) => {
    const value = e.target.value;
    const selectedText = e.target.options[e.target.selectedIndex].text;
    const selectedOption = (printSizeInput?.options || []).find(
      (option) => option?.value?.toString() === value,
    );

    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...prevState.meta,
        xAspectRatio: selectedOption?.xAspectRatio,
        yAspectRatio: selectedOption?.yAspectRatio,
        aspectRatio:
          selectedOption?.xAspectRatio && selectedOption?.yAspectRatio
            ? selectedOption.xAspectRatio / selectedOption.yAspectRatio
            : undefined,
        matType: undefined,
        matOpeningShape: undefined,
      },
      inputs: {
        ...prevState.inputs,
        printSize: {
          ...prevState.inputs.printSize,
          selectedValue: value,
          selectedValueText: selectedText,
          selectedValuePrice: selectedOption?.price,
        },
      },
    }));
  };

  const handleGenericSelectChange = (key, e) => {
    const input = form?.inputs?.[key];
    if (!input) return;

    setForm((prevState) => {
      const resetInputs = resetFormInputs(prevState.inputs, key);
      const selectedOption = input.options.find(
        (option) => option?.value?.toString() === e.target.value,
      );

      resetInputs[key] = {
        ...resetInputs[key],
        selectedValue: e.target.value,
        selectedValueText: e.target.options[e.target.selectedIndex].text,
        selectedValuePrice: selectedOption?.price,
      };

      const newMeta = { ...prevState.meta };
      const metaResets = getMetaResets(key, prevState);
      if (metaResets.finishingCategoryId) newMeta.finishingCategoryId = undefined;
      if (metaResets.finishingServiceId) newMeta.finishingServiceId = undefined;
      if (metaResets.finishingDetailId) newMeta.finishingDetailId = undefined;
      if (MAT_UPSTREAM_KEYS.has(key)) {
        newMeta.matType = undefined;
        newMeta.matOpeningShape = undefined;
      }

      return { meta: newMeta, inputs: resetInputs };
    });
  };

  const handleAcrylicChange = (e) => {
    if (e.target.value === 'none') {
      setForm((prevState) => ({
        ...prevState,
        meta: {
          ...prevState.meta,
          finishingServiceId:
            prevState.inputs?.frame?.selectedValue ||
            prevState.meta.finishingServiceId,
          finishingDetailId:
            prevState.inputs?.frameStain?.selectedValue ||
            prevState.meta.finishingDetailId,
          requiredInputs: HARDWOOD_FRAME_TYPES.includes(
            prevState.inputs?.frameType?.selectedValueText,
          )
            ? ['Frame Stain']
            : [],
        },
        inputs: {
          ...prevState.inputs,
          acrylic: {
            ...prevState.inputs.acrylic,
            selectedValue: 'none',
            selectedValueText: 'None',
            selectedValuePrice: 0,
          },
          ...(HARDWOOD_FRAME_TYPES.includes(
            prevState.inputs?.frameType?.selectedValueText,
          ) && { frameStain: prevState.inputs.frameStain }),
        },
      }));
      return;
    }

    const value = e.target.value;
    const selectedText = e.target.options[e.target.selectedIndex].text;
    const selectedOption = (acrylicInput?.options || []).find(
      (option) => option?.value?.toString() === value,
    );

    setForm((prevState) => {
      const isHardwood = HARDWOOD_FRAME_TYPES.includes(
        prevState.inputs?.frameType?.selectedValueText,
      );
      return {
        meta: {
          ...prevState.meta,
          finishingServiceId: undefined,
          finishingDetailId: undefined,
        },
        inputs: {
          ...prevState.inputs,
          acrylic: {
            ...prevState.inputs.acrylic,
            selectedValue: value,
            selectedValueText: selectedText,
            selectedValuePrice: selectedOption?.price,
          },
          ...(isHardwood && {
            frameStain: {
              ...(prevState.inputs.frameStain || defaultOptions.frameStain),
              selectedValue: undefined,
              selectedValueText: undefined,
              selectedValuePrice: undefined,
              isLoading: true,
              isDisabled: false,
            },
          }),
        },
      };
    });
  };

  const handleFrameMatChange = (e) => {
    if (e.target.value === 'none') {
      setForm((prevState) => ({
        ...prevState,
        meta: {
          ...prevState.meta,
          matType: undefined,
          matOpeningShape: undefined,
        },
        inputs: {
          ...prevState.inputs,
          frameMat: {
            ...prevState.inputs.frameMat,
            selectedValue: 'none',
            selectedValueText: 'None',
          },
        },
      }));
      return;
    }
    handleGenericSelectChange('frameMat', e);
  };

  const handleFrameStainChange = (e) => {
    if (e.target.value === 'none') {
      setForm((prevState) => ({
        ...prevState,
        meta: {
          ...prevState.meta,
          finishingServiceId:
            prevState.inputs?.frame?.selectedValue ||
            prevState.inputs?.acrylic?.selectedValue ||
            prevState.meta.finishingServiceId,
          finishingDetailId: undefined,
          requiredInputs: [],
        },
        inputs: {
          ...prevState.inputs,
          frameStain: {
            ...prevState.inputs.frameStain,
            selectedValue: 'none',
            selectedValueText: 'None',
          },
        },
      }));
      return;
    }
    handleGenericSelectChange('frameStain', e);
  };

  if (!form?.inputs) return null;

  const renderSelect = ({ input, value, onChange, fallbackLabel, disabled, filterOption }) => (
    <div className="input-container paper-type">
      <div className="label-container">
        <div className="div-block-150">
          <div className="sub-label-form">{input?.label || fallbackLabel}</div>
        </div>
      </div>
      <div className="form-block-input w-form">
        <form className="form-input select-field">
          <select
            className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
            style={{ width: '100%' }}
            onChange={onChange}
            value={value || 'default'}
            disabled={disabled ?? input?.isDisabled}
          >
            {input?.isLoading && (input?.options?.length || 0) === 0 ? (
              <option value="">Loading...</option>
            ) : (
              <option value="default">Choose One</option>
            )}
            {(input?.options || [])
              .filter((option) => !option?.text?.endsWith('**'))
              .filter(filterOption || Boolean)
              .map((option) => {
                if (!option?.value || !option?.text) return null;
                return (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                );
              })
              .filter(Boolean)}
          </select>
          <img
            src={ChevronIcon}
            alt=""
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              width: '16px',
              height: '16px',
            }}
          />
        </form>
      </div>
    </div>
  );

  return (
    <div className={styles.printsFormStack}>
      <div className="input-container quantity">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">Quantity</div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <div className="input-wrapper">
            <input
              ref={quantityRef}
              type="number"
              min="1"
              maxLength="256"
              name="field-quantity"
              defaultValue="1"
              className="text-field-2 data-jscolor w-input"
              pattern="[0-9]*"
              inputMode="numeric"
            />
          </div>
        </div>
      </div>

      {/* Paper type */}
      <div className="input-container paper-type">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">
              {paperTypeInput?.label || 'Paper type'}
            </div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <form className="form-input select-field">
            <select
              className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
              style={{ width: '100%' }}
              onChange={handlePaperTypeChange}
              value={paperTypeSelectedValue || 'default'}
            >
              {paperTypeInput?.isLoading &&
              (paperTypeInput?.options?.length || 0) === 0 ? (
                <option value="">Loading...</option>
              ) : (
                <option value="default">Choose One</option>
              )}
              {(paperTypeInput?.options || [])
                .filter(filterPaperOption)
                .filter((option) => !option.text?.endsWith('**'))
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {stripPrice(option.text)}
                  </option>
                ))}
            </select>
            <img
              src={ChevronIcon}
              alt=""
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                width: '16px',
                height: '16px',
              }}
            />
          </form>
        </div>
      </div>

      {/* Print size */}
      <div className="input-container paper-type">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">
              {printSizeInput?.label || 'Print size'}
            </div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <form className="form-input select-field">
            <select
              className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
              style={{ width: '100%' }}
              onChange={handlePrintSizeChange}
              value={printSizeInput?.selectedValue || 'default'}
              disabled={printSizeInput?.isDisabled}
            >
              {printSizeInput?.isLoading &&
              (printSizeInput?.options?.length || 0) === 0 ? (
                <option value="">Loading...</option>
              ) : (
                <option value="default">Choose One</option>
              )}
              {(printSizeInput?.options || [])
                .filter((option) => !option.text?.endsWith('**'))
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
            </select>
            <img
              src={ChevronIcon}
              alt=""
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                width: '16px',
                height: '16px',
              }}
            />
          </form>
        </div>
      </div>

      {frameTypeInput &&
        renderSelect({
          input: frameTypeInput,
          value: frameTypeInput?.selectedValue,
          onChange: (e) => handleGenericSelectChange('frameType', e),
          fallbackLabel: 'Frame type',
          filterOption: (option) => !isCircleOrOvalFrameType(option?.text),
        })}

      {frameInput &&
        !isCircleOrOvalFrame &&
        renderSelect({
          input: frameInput,
          value: frameInput?.selectedValue,
          onChange: (e) => handleGenericSelectChange('frame', e),
          fallbackLabel: 'Frame',
        })}

      {frameMatInput &&
        renderSelect({
          input: frameMatInput,
          value: frameMatInput?.selectedValue,
          onChange: handleFrameMatChange,
          fallbackLabel: 'Frame mat',
        })}

      {hasRealFrameMat &&
        renderSelect({
          input: {
            label: 'Mat Type',
            options: matTypeOptions,
          },
          value: matType,
          onChange: (e) => setMatType(e.target.value),
          fallbackLabel: 'Mat Type',
        })}

      {hasRealFrameMat &&
        matType !== 'no-front-mat' &&
        matOpeningShapeOptions.length > 1 &&
        renderSelect({
          input: {
            label: 'Mat Opening Shape',
            options: matOpeningShapeOptions,
          },
          value: matOpeningShape,
          onChange: (e) => setMatOpeningShape(e.target.value),
          fallbackLabel: 'Mat Opening Shape',
          disabled: isCircleDeckle,
        })}

      {acrylicInput &&
        renderSelect({
          input: acrylicInput,
          value: acrylicInput?.selectedValue,
          onChange: handleAcrylicChange,
          fallbackLabel: 'Acrylic',
        })}

      {frameStainInput &&
        (isHardwoodFrame || (frameStainInput?.options?.length || 0) > 0) &&
        renderSelect({
          input: frameStainInput,
          value: frameStainInput?.selectedValue,
          onChange: handleFrameStainChange,
          fallbackLabel: 'Frame stain',
        })}
    </div>
  );
};

export default FramingFormInputs;
